import type { Key } from "react";
import type { DashboardBucket, DashboardFilters } from "@/types";

import { Segment } from "@heroui-pro/react";
import { useState } from "react";

import { AppPage } from "@/layouts/page";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  BreakdownChart,
  OrdersChart,
  RevenueChart,
} from "@/components/dashboard/charts";
import { DashboardKpis } from "@/components/dashboard/kpis";
import {
  LossItemsTable,
  TopOutOfStockTable,
  TopProductsTable,
} from "@/components/dashboard/tables";
import {
  InlineFilterSelect,
  PlainDateRangeField,
  type DayRange,
} from "@/components/kit";
import { Toolbar, formatDay } from "@/components/sales/shared";
import { DeliveryType } from "@/enums/delivery";
import { useGetDashboard } from "@/hooks/api/dashboard/useGetDashboard";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { useGetAllSuppliers } from "@/hooks/api/supplier";

type Preset = "today" | "7d" | "30d" | "mtd";

const PRESETS: { id: Preset; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "mtd", label: "Month to date" },
];

/** The business runs on Dubai days, whatever the admin's own time zone. */
const dubaiToday = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Dubai" }).format(
    new Date(),
  );

const shiftDay = (day: string, days: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000)
    .toISOString()
    .slice(0, 10);

function presetRange(preset: Preset): DayRange {
  const today = dubaiToday();

  switch (preset) {
    case "today":
      return { start: today, end: today };
    case "7d":
      return { start: shiftDay(today, -6), end: today };
    case "30d":
      return { start: shiftDay(today, -29), end: today };
    case "mtd":
      return { start: `${today.slice(0, 8)}01`, end: today };
  }
}

export function DashboardPage() {
  const [preset, setPreset] = useState<Preset | null>("30d");
  const [range, setRange] = useState<DayRange>(() => presetRange("30d"));
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [deliveryType, setDeliveryType] = useState<string | null>(null);
  const [bucket, setBucket] = useState<string | null>(null);

  const filters: DashboardFilters = {
    from: range.start,
    to: range.end,
    supplierId: supplierId ?? undefined,
    categoryId: categoryId ?? undefined,
    deliveryType: (deliveryType as DeliveryType | null) ?? undefined,
    bucket: (bucket as DashboardBucket | null) ?? undefined,
  };
  const { data, isFetching, error } = useGetDashboard(filters);
  const { data: suppliers = [] } = useGetAllSuppliers();
  const { data: categories = [] } = useGetAllCategories();

  return (
    <AppPage
      subtitle={
        data
          ? `Compared with ${formatDay(data.range.previousFrom)} to ${formatDay(data.range.previousTo)}. Money excludes VAT.`
          : undefined
      }
      title="Dashboard"
    >
      <Toolbar end={isFetching ? <LoadingSpinner size="sm" /> : null}>
        <Segment
          aria-label="Date range"
          selectedKey={preset}
          size="sm"
          onSelectionChange={(key: Key) => {
            setPreset(key as Preset);
            setRange(presetRange(key as Preset));
          }}
        >
          {PRESETS.map((option, index) => (
            <Segment.Item key={option.id} id={option.id}>
              {index > 0 ? <Segment.Separator /> : null}
              {option.label}
            </Segment.Item>
          ))}
        </Segment>
        <div className="[&_[data-slot=label]]:sr-only">
          <PlainDateRangeField
            label="Custom dates"
            name="dashboardRange"
            value={range}
            onChange={(next) => {
              // Wait for both ends; a half-picked range would be rejected.
              if (!next.start || !next.end) return;
              setPreset(null);
              setRange(next);
            }}
          />
        </div>
        <InlineFilterSelect
          label="Supplier"
          options={[
            ...suppliers.map((s) => ({ id: s.id, label: s.name })),
            { id: "order-anything", label: "Order anything" },
          ]}
          value={supplierId}
          onChange={setSupplierId}
        />
        <InlineFilterSelect
          label="Category"
          options={categories.map((c) => ({ id: c.id, label: c.name }))}
          value={categoryId}
          onChange={setCategoryId}
        />
        <InlineFilterSelect
          label="Delivery"
          options={[
            { id: DeliveryType.EXPRESS, label: "Express" },
            { id: DeliveryType.SCHEDULED, label: "Scheduled" },
          ]}
          value={deliveryType}
          onChange={setDeliveryType}
        />
        <InlineFilterSelect
          allLabel="Order placed"
          label="Day by"
          options={[{ id: "delivery", label: "Delivery day" }]}
          value={bucket}
          onChange={setBucket}
        />
      </Toolbar>

      {error && !data ? (
        <p className="text-danger py-16 text-center text-sm">
          Couldn&apos;t load the dashboard. Check the dates and try again.
        </p>
      ) : !data ? (
        <div className="flex justify-center py-24">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <DashboardKpis data={data} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <RevenueChart data={data} />
            <OrdersChart data={data} />
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <BreakdownChart
              data={data}
              description="Bought items with a buy price, fulfilled orders"
              metric="grossProfit"
              title="Gross profit"
            />
            <BreakdownChart
              data={data}
              description="Every placed order"
              metric="units"
              title="Units ordered"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
            <TopProductsTable data={data} />
            <TopOutOfStockTable data={data} />
          </div>
          <LossItemsTable data={data} />
        </div>
      )}
    </AppPage>
  );
}
